import { normalizeAnimeTitle } from '../utils/helpers';

/**
 * MyAnimeList "Scored" lookup through our /api/mal/search proxy (MAL's own search JSON;
 * Jikan is often unreachable). Lookups go through one throttled queue so a board full of
 * cards doesn't hammer MAL, and every result (including "not on MAL") is cached in localStorage.
 */

export interface MalScore {
  score: number;
  malId: number;
  url: string;
}

interface CacheEntry {
  s: MalScore | null;
  t: number; // ms — when it was looked up
}

const CACHE_KEY = 'mal-score-cache-v5';
const FOUND_TTL = 3 * 24 * 60 * 60 * 1000; // scores move slowly while a show airs
const MISS_TTL = 24 * 60 * 60 * 1000;
const REQUEST_GAP_MS = 1000;

let cache: Record<string, CacheEntry> | null = null;
const inflight = new Map<string, Promise<MalScore | null>>();
let queueTail: Promise<unknown> = Promise.resolve();

function loadCache(): Record<string, CacheEntry> {
  if (cache) return cache;
  try {
    cache = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}') || {};
  } catch {
    cache = {};
  }
  return cache!;
}

function saveCache() {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache || {}));
  } catch {
    // storage full / blocked: the in-memory cache still serves this visit
  }
}

/** The searchable part of a stored title: no Thai, no sub/dub tags, no brackets */
export function malQueryTitle(title?: string): string {
  return (title || '')
    .replace(/[฀-๿]+/g, ' ')
    .replace(/【[^】]*】|\[[^\]]*\]|\((?:ซับไทย|พากย์ไทย|uncut|uncensored)?\s*\)/gi, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^[\s\-–:|]+|[\s\-–:|]+$/g, '')
    .trim();
}

const ROMAN_SEASONS: Record<string, number> = { II: 2, III: 3, IV: 4, V: 5 };

/**
 * normalizeAnimeTitle reads "Ranma ½ (2024)" as season 2024 (drop the year first) and doesn't
 * know MAL's roman sequels ("Tensei shitara Ken deshita II" = season 2)
 */
function norm(title: string): string {
  return normalizeAnimeTitle(
    title
      .replace(/\(\s*(?:19|20)\d{2}\s*\)/g, ' ')
      .replace(/\s(II|III|IV|V)(?=\s|:|$)/gi, (_, r: string) => ` season ${ROMAN_SEASONS[r.toUpperCase()]}`)
  );
}

function seasonNo(normalized: string): string {
  const m = normalized.match(/s(\d+)/);
  return m ? String(parseInt(m[1], 10)) : '1';
}

/** Season of one of our titles; a bare trailing number counts too ("Re:Zero 4", not "Kaiju No. 8") */
function querySeason(query: string): string {
  const m = query.match(/\s([2-9])\s*$/);
  if (m && !/\bno\.?\s*\d\s*$/i.test(query)) return m[1];
  return seasonNo(norm(query));
}

const STOP_WORDS = new Set(['the', 'season', 'part', 'and', 'with', 'from', 'animation']);
function words(title: string): Set<string> {
  return new Set(
    title
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter((w) => w.length >= 3 && !STOP_WORDS.has(w))
  );
}

const EXTRA_CUT = /\b(recap|specials?|picture drama|pv|cm|ova|movie)\b/i;

function bigrams(s: string): string[] {
  const out: string[] = [];
  for (let i = 0; i < s.length - 1; i++) out.push(s.slice(i, i + 2));
  return out;
}

function similarity(a: string, b: string): number {
  if (!a || !b) return 0;
  if (a === b) return 1;
  const ba = bigrams(a);
  const pool = bigrams(b);
  if (!ba.length || !pool.length) return 0;
  let common = 0;
  for (const g of ba) {
    const i = pool.indexOf(g);
    if (i >= 0) {
      common++;
      pool.splice(i, 1);
    }
  }
  return (2 * common) / (ba.length + bigrams(b).length);
}

export interface MalSearchItem {
  id: number;
  name: string;
  url: string;
  year: number;
  score: number;
}

function pickBest(query: string, year: number, items: MalSearchItem[]): MalSearchItem | null {
  const q = norm(query);
  const qSeason = querySeason(query);
  const yearGap = (it: MalSearchItem) => (year && it.year ? Math.abs(it.year - year) : 0);
  // "Season 2" on our side must not land on the first season's page (and vice versa),
  // and a recap/special isn't the show unless that's what we asked for
  const candidates = items.filter(
    (it) =>
      seasonNo(norm(it.name)) === qSeason && (EXTRA_CUT.test(query) || !EXTRA_CUT.test(it.name))
  );
  let best: MalSearchItem | null = null;
  let bestScore = 0;
  for (const it of candidates) {
    const n = norm(it.name);
    const score = similarity(q, n) - Math.min(0.3, yearGap(it) * 0.1);
    if (score > bestScore) {
      bestScore = score;
      best = it;
    }
  }
  if (bestScore >= 0.6) return best;
  // English titles don't resemble MAL's romaji names ("Reborn as a Goblin" = "Tensei Goblin…"),
  // but MAL's own search ranks the right show first; it must still share a word with ours
  const qWords = words(query);
  return (
    candidates.find(
      (it) => yearGap(it) <= 1 && [...words(it.name)].some((w) => qWords.has(w))
    ) || null
  );
}

/** Runs fetches one at a time, REQUEST_GAP_MS apart */
function throttled<T>(job: () => Promise<T>): Promise<T> {
  const run = queueTail.then(job, job);
  queueTail = run
    .catch(() => undefined)
    .then(() => new Promise((r) => setTimeout(r, REQUEST_GAP_MS)));
  return run;
}

async function searchMal(query: string, attempt = 0): Promise<MalSearchItem[] | null> {
  const resp = await fetch(`/api/mal/search?q=${encodeURIComponent(query)}`);
  // MAL answers 405 for a while after too many requests: back off, then give up uncached
  if (resp.status === 502 && attempt < 2) {
    await new Promise((r) => setTimeout(r, 4000 * (attempt + 1)));
    return searchMal(query, attempt + 1);
  }
  if (!resp.ok) return null; // transient: don't cache
  const json = await resp.json();
  return Array.isArray(json?.items) ? json.items : null;
}

export function getCachedMalScore(title?: string, year = 0): MalScore | null | undefined {
  const query = malQueryTitle(title);
  if (query.length < 2) return null;
  const entry = loadCache()[`${query.toLowerCase()}|${year}`];
  if (!entry) return undefined;
  const ttl = entry.s ? FOUND_TTL : MISS_TTL;
  return Date.now() - entry.t < ttl ? entry.s : undefined;
}

export function fetchMalScore(title?: string, year = 0): Promise<MalScore | null> {
  const query = malQueryTitle(title);
  if (query.length < 2) return Promise.resolve(null);
  const key = `${query.toLowerCase()}|${year}`;
  const cached = getCachedMalScore(title, year);
  if (cached !== undefined) return Promise.resolve(cached);
  if (inflight.has(key)) return inflight.get(key)!;

  const p = throttled(async () => {
    let items = await searchMal(query).catch(() => null);
    // MAL finds nothing for doubled season markers ("Ken deshita II Season 2"); retry without
    // the "Season N" part — pickBest still checks the season against the full title
    const bare = query.replace(/\s+(?:season\s*\d+|\d+(?:st|nd|rd|th)\s+season|part\s*\d+)\s*$/i, '');
    if (items && !items.length && bare !== query) {
      await new Promise((r) => setTimeout(r, REQUEST_GAP_MS));
      items = await searchMal(bare).catch(() => null);
    }
    // An empty list is what a throttled MAL sends too; only cache a real answer
    if (!items?.length) return null;
    const best = pickBest(query, year, items);
    // Not-yet-aired shows have "N/A" on MAL: nothing to show, but they did match
    const found: MalScore | null =
      best && best.score > 0 ? { score: best.score, malId: best.id, url: best.url } : null;
    loadCache()[key] = { s: found, t: Date.now() };
    saveCache();
    return found;
  }).finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
}

/**
 * Manual "check rating" search: MAL's results as-is, for the user to pick from. Skips the
 * queue — the user is waiting, and the cards' background lookups can be minutes long.
 */
export function searchMalTitles(query: string): Promise<MalSearchItem[] | null> {
  const q = malQueryTitle(query);
  if (q.length < 2) return Promise.resolve([]);
  return searchMal(q).catch(() => null);
}

/** Cache the user's pick under its own name so cards show it without another lookup */
export function rememberMalPick(item: MalSearchItem) {
  const key = `${malQueryTitle(item.name).toLowerCase()}|0`;
  loadCache()[key] = {
    s: item.score > 0 ? { score: item.score, malId: item.id, url: item.url } : null,
    t: Date.now(),
  };
  saveCache();
}
